// ─── Lead Engine Service ─────────────────────────────────────────────────────
// Central orchestration for the lead gen system
// All lead sources normalize into one master payload and flow through here
// Part of the Taigenic lead intelligence layer
// ─────────────────────────────────────────────────────────────────────────────

import { supabase, isSupabaseAvailable } from '../lib/supabaseClient'
import { autoLinkLead } from './leadProspectBridgeService'

// ─── Edge function proxy (admin-leads, admin JWT required) ──────────────────
export async function callLeadsEdge(action: string, params: Record<string, unknown> = {}): Promise<{ success: boolean; data?: any; error?: string }> {
  if (!supabase) return { success: false, error: 'Supabase not configured' }
  try {
    const { data, error } = await supabase.functions.invoke('admin-leads', { body: { action, ...params } })
    if (error) return { success: false, error: (error as any).message || String(error) }
    if (!data?.success) return { success: false, error: data?.error || 'Edge error' }
    return { success: true, data: data.data }
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' }
  }
}
import { scoreLead, getLeadPriority, getLeadValueBand } from './leadScoringService'
import type { LeadPayload } from './leadScoringService'
import { resolveLeadDestination } from './leadRoutingService'
import { sendPartnerLeadNotification, sendInternalLeadNotification, sendEnquirerConfirmation } from './leadNotificationService'

// ─── Types ───────────────────────────────────────────────────────────────────

export type LeadStatus =
  | 'new'
  | 'qualified'
  | 'engaged'
  | 'proposal_sent'
  | 'booked'
  | 'lost'
  | 'spam'

export type LeadEventType =
  | 'lead_created'
  | 'lead_scored'
  | 'partner_notified'
  | 'internal_notified'
  | 'partner_opened'
  | 'partner_replied'
  | 'status_changed'
  | 'lead_booked'
  | 'lead_lost'

export interface CreateLeadResult {
  success: boolean
  leadId: string | null
  score: number
  priority: string
  error: any
  notificationFailed?: boolean   // true when lead saved but partner/confirmation email failed
  routingFailed?: boolean        // true when listing_id present but no partner email found
}

// ─── Core Functions ──────────────────────────────────────────────────────────

/**
 * Create a new lead - the single entry point for all lead sources
 *
 * Flow:
 * 1. Normalize and validate payload
 * 2. Score the lead
 * 3. Insert into leads table
 * 4. Record lead_created event
 * 5. Store initial message in lead_messages
 * 6. Route and send notifications
 * 7. Return result
 */
// Valid UUID v4 check, integer IDs from static data must not be sent to UUID columns
const toUUID = (v: any): string | null => {
  if (!v) return null
  const s = String(v)
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ? s : null
}

export async function createLead(payload: LeadPayload): Promise<CreateLeadResult> {
  try {
    // 1. Score the lead
    const score = scoreLead(payload)
    const priority = getLeadPriority(score)
    const valueBand = getLeadValueBand(payload)

    // 2. Build the database row
    const leadRow = {
      lead_source: payload.leadSource || 'unknown',
      lead_channel: payload.leadChannel || 'form',
      lead_type: payload.leadType || 'venue_enquiry',

      status: 'new' as LeadStatus,
      priority,
      score,

      listing_id: toUUID(payload.listingId),
      listing_type: payload.listingType || null,
      venue_id: toUUID(payload.venueId),
      vendor_id: toUUID(payload.vendorId),

      user_id: payload.userId || null,
      aura_session_id: payload.auraSessionId || null,
      conversation_id: payload.conversationId || null,

      first_name: payload.firstName || null,
      last_name: payload.lastName || null,
      full_name: payload.fullName || buildFullName(payload.firstName, payload.lastName),
      email: payload.email || null,
      phone: payload.phone || null,
      preferred_contact_method: payload.preferredContactMethod || null,

      wedding_date: payload.weddingDate || null,
      wedding_month: payload.weddingMonth || null,
      wedding_year: payload.weddingYear || null,
      exact_date_known: payload.exactDateKnown || false,
      guest_count: payload.guestCount || null,
      budget_range: payload.budgetRange || null,
      location_preference: payload.locationPreference || null,
      event_location: payload.eventLocation || null,

      message: payload.message || null,
      intent_summary: payload.intentSummary || null,
      requirements_json: payload.requirementsJson || {},
      tags_json: payload.tagsJson || [],

      consent_marketing: payload.consentMarketing || false,
      consent_data_processing: payload.consentDataProcessing !== false,

      lead_value_band: valueBand,
    }

    // 3. Validate listing_id for routable enquiry types before hitting the DB
    const resolvedListingId = toUUID(payload.listingId)
    if (
      (payload.leadType === 'venue_enquiry' || payload.leadType === 'vendor_enquiry') &&
      !resolvedListingId
    ) {
      return {
        success: false, leadId: null, score, priority,
        error: new Error('This listing could not be found. Please refresh the page and try again.'),
      }
    }

    if (!isSupabaseAvailable()) {
      console.log('leadEngineService: Supabase unavailable, logging lead locally')
      return { success: true, leadId: 'offline-' + Date.now(), score, priority, error: null }
    }

    // Dedup: check for existing lead with same email before creating (via edge function, bypasses RLS)
    let leadId: string | null = null
    let isNewLead = true
    if (payload.email) {
      const emailLower = payload.email.trim().toLowerCase()
      // Dedup by email via a direct select. Anonymous enquirers have no SELECT
      // policy on leads, so this returns nothing for them (they always create a
      // fresh lead); authenticated/admin contexts still find and merge duplicates.
      const { data: existingRows } = await supabase
        .from('leads').select('id,status,email').ilike('email', emailLower).limit(1)
      const existing = (existingRows || [])[0] as Record<string, unknown> | undefined

      if (existing?.id) {
        // Update existing lead with fresh data rather than creating a duplicate
        console.log('leadEngineService: duplicate email detected, updating existing lead', existing.id)
        const mergeUpdates: Record<string, unknown> = { score, updated_at: new Date().toISOString() }
        if (leadRow.phone && !existing.status) mergeUpdates.phone = leadRow.phone
        if (leadRow.requirements_json && Object.keys(leadRow.requirements_json).length)
          mergeUpdates.requirements_json = leadRow.requirements_json
        if (leadRow.message) mergeUpdates.message = leadRow.message
        await callLeadsEdge('update_field', { leadId: existing.id, updates: mergeUpdates })
        await callLeadsEdge('insert_event', {
          leadId: existing.id,
          eventType: 'resubmitted',
          eventData: { source: payload.leadSource, channel: payload.leadChannel, score },
        })
        leadId = existing.id
        isNewLead = false
      }
    }

    if (isNewLead) {
      // Public enquiries are anonymous. Insert directly under the anon RLS policy
      // (leads_anon_insert) instead of the admin-only edge function. The id is
      // generated client-side so we never need a RETURNING/SELECT (anon has no
      // SELECT policy on leads). This is what makes listing + showcase enquiries
      // actually capture for logged-out couples.
      const newId =
        (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
          ? globalThis.crypto.randomUUID()
          : `lead-${Date.now()}-${Math.random().toString(36).slice(2)}`
      console.log('leadEngineService: inserting lead', newId)
      const { error: insertErr } = await supabase.from('leads').insert({ id: newId, ...leadRow })
      if (insertErr) {
        console.error('leadEngineService: leads INSERT failed,', insertErr)
        throw new Error(insertErr.message || 'Failed to create lead')
      }
      leadId = newId
      console.log('leadEngineService: lead created', leadId)
    }

    // Auto-link to any existing B2B prospect with the same email (fire-and-forget)
    if (isNewLead && leadId && payload.email) {
      autoLinkLead(leadId, payload.email).catch(() => {})
    }

    // 4. Record lead_created event (new leads only)
    if (isNewLead) {
      await recordLeadEvent(leadId!, 'lead_created', {
        source: payload.leadSource,
        channel: payload.leadChannel,
        score,
        priority,
      })
    }

    // 5. Store initial message, blocking: a lead without its original message is not useful
    if (isNewLead && payload.message) {
      // Direct insert under the anon RLS policy (lead_messages_anon_insert).
      const { error: msgErr } = await supabase.from('lead_messages').insert({
        lead_id: leadId,
        message_type: 'initial_enquiry',
        body: payload.message,
      })
      if (msgErr) {
        console.warn('leadEngineService: message insert failed:', msgErr)
        await recordLeadEvent(leadId!, 'message_insert_failed', { error: String(msgErr.message || msgErr) })
      }
    }

    // 6. Route and send notifications, awaited so failures are visible in the result.
    //    Lead is already saved; this step must not silently fail.
    let notificationFailed = false
    let routingFailed = false
    if (isNewLead) {
      const notifyResult = await routeAndNotify(leadId!, leadRow)
      notificationFailed = notifyResult.notificationFailed
      routingFailed      = notifyResult.routingFailed
    }

    return { success: true, leadId: leadId!, score, priority, error: null, notificationFailed, routingFailed }
  } catch (error) {
    console.error('leadEngineService: Failed to create lead:', error)
    return { success: false, leadId: null, score: 0, priority: 'normal', error }
  }
}

/**
 * Get a lead by ID
 */
export async function getLeadById(leadId: string) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase unavailable') }

  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', leadId)
    .single()

  return { data, error }
}

/**
 * Update a lead
 */
export async function updateLead(leadId: string, updates: Partial<Record<string, any>>) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase unavailable') }

  const { data, error } = await supabase
    .from('leads')
    .update(updates)
    .eq('id', leadId)
    .select()
    .single()

  return { data, error }
}

/**
 * Update lead status with event logging
 */
export async function updateLeadStatus(leadId: string, newStatus: LeadStatus) {
  const result = await updateLead(leadId, { status: newStatus })

  if (!result.error) {
    await recordLeadEvent(leadId, 'status_changed', { new_status: newStatus })

    // Record lifecycle timestamps
    const timestampMap: Partial<Record<LeadStatus, string>> = {
      engaged: 'engaged_at',
      proposal_sent: 'proposal_sent_at',
      booked: 'booked_at',
      lost: 'lost_at',
    }

    const tsField = timestampMap[newStatus]
    if (tsField) {
      await updateLead(leadId, { [tsField]: new Date().toISOString() })
    }
  }

  return result
}

/**
 * Record a timeline event for a lead
 */
export async function recordLeadEvent(
  leadId: string,
  eventType: LeadEventType | string,
  eventData: Record<string, any> = {},
  eventLabel?: string
) {
  if (!isSupabaseAvailable()) {
    console.log(`leadEngineService: Event [${eventType}] for lead ${leadId}:`, eventData)
    return
  }

  try {
    // Direct insert under the anon RLS policy (lead_events_anon_insert) so public
    // enquiries can log events without the admin-only edge function.
    const row: Record<string, unknown> = { lead_id: leadId, event_type: eventType, event_data: eventData }
    if (eventLabel) row.event_label = eventLabel
    await supabase.from('lead_events').insert(row)
  } catch (err) {
    console.warn('leadEngineService: Failed to record event:', err)
  }
}

/**
 * Attach conversation context from Aura to a lead
 */
export async function attachConversationContext(
  leadId: string,
  context: {
    auraSessionId?: string
    conversationId?: string
    rawSummary?: string
    intentSummary?: string
    requirementsJson?: Record<string, any>
  }
) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase unavailable') }

  const { data, error } = await supabase.from('lead_conversations').insert({
    lead_id: leadId,
    aura_session_id: context.auraSessionId || null,
    conversation_id: context.conversationId || null,
    raw_summary: context.rawSummary || null,
    intent_summary: context.intentSummary || null,
    requirements_json: context.requirementsJson || {},
  })

  if (!error) {
    await recordLeadEvent(leadId, 'conversation_attached', {
      aura_session_id: context.auraSessionId,
      has_intent: !!context.intentSummary,
    })
  }

  return { data, error }
}

/**
 * List leads with filters
 */
export async function listLeads(filters: {
  status?: string
  leadSource?: string
  leadType?: string
  vendorId?: string
  venueId?: string
  minScore?: number
  limit?: number
  offset?: number
} = {}) {
  if (!isSupabaseAvailable()) return { data: [], error: new Error('Supabase unavailable'), count: 0 }

  let query = supabase
    .from('leads')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })

  if (filters.status) query = query.eq('status', filters.status)
  if (filters.leadSource) query = query.eq('lead_source', filters.leadSource)
  if (filters.leadType) query = query.eq('lead_type', filters.leadType)
  if (filters.vendorId) query = query.eq('vendor_id', filters.vendorId)
  if (filters.venueId) query = query.eq('venue_id', filters.venueId)
  if (filters.minScore) query = query.gte('score', filters.minScore)

  const limit = filters.limit || 50
  const offset = filters.offset || 0
  query = query.range(offset, offset + limit - 1)

  const { data, error, count } = await query
  return { data: data || [], error, count: count || 0 }
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

function buildFullName(first?: string, last?: string): string | null {
  const parts = [first, last].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

interface NotifyResult {
  partnerNotified: boolean
  confirmationSent: boolean
  internalNotified: boolean
  routingFailed: boolean    // listing_id present but no partner email found
  notificationFailed: boolean
}

/**
 * Internal: route a lead and send all notifications.
 * Returns a result object, never throws. Failures are logged and returned.
 */
async function routeAndNotify(leadId: string, leadRow: Record<string, any>): Promise<NotifyResult> {
  const result: NotifyResult = {
    partnerNotified: false,
    confirmationSent: false,
    internalNotified: false,
    routingFailed: false,
    notificationFailed: false,
  }

  try {
    const destination = await resolveLeadDestination({
      listingId: leadRow.listing_id,
      vendorId:  leadRow.vendor_id,
      venueId:   leadRow.venue_id,
    })

    const leadRecord = { id: leadId, ...leadRow } as any

    // Detect routing failure: had a listing_id but couldn't find a partner email
    if (leadRow.listing_id && destination.routeType === 'internal_only') {
      result.routingFailed = true
      await recordLeadEvent(leadId, 'routing_failed', {
        listing_id: leadRow.listing_id,
        reason: 'No partner email found for listing',
      })
    }

    // 1. Send confirmation to the enquirer (Fix 1, was never called before)
    const confirmResult = await sendEnquirerConfirmation(leadRecord, destination.partnerName)
    result.confirmationSent = confirmResult.success

    // 2. Send partner notification
    if (destination.partnerEmail) {
      const partnerResult = await sendPartnerLeadNotification(
        leadRecord,
        destination.partnerEmail,
        destination.partnerName
      )
      result.partnerNotified = partnerResult.success
      if (partnerResult.success) {
        await recordLeadEvent(leadId, 'partner_notified', { email: destination.partnerEmail })
        await updateLead(leadId, {
          vendor_notified_at: new Date().toISOString(),
          status: 'engaged',
        })
      }
    }

    // 3. Send internal notification (always), include routing failure flag in subject if needed
    const internalResult = await sendInternalLeadNotification(leadRecord, result.routingFailed)
    result.internalNotified = internalResult.success
    if (internalResult.success) {
      await recordLeadEvent(leadId, 'internal_notified', { email: destination.internalEmail })
      await updateLead(leadId, { internal_notified_at: new Date().toISOString() })
    }

    result.notificationFailed = !result.confirmationSent ||
      (!!destination.partnerEmail && !result.partnerNotified) ||
      !result.internalNotified

  } catch (err) {
    console.error('leadEngineService: routeAndNotify failed:', err)
    result.notificationFailed = true
  }

  return result
}
